"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";

import { HeroBanner } from "@/composite/HeroBanner";
import type { CTAField, HeroReference } from "@/types/references";
import { resolveImageProps } from "@/app/utilities/images";

interface ConsultantInfo {
	image?: string;
	imageAltText?: string;
	description: string;
	cta: CTAField;
}

/**
 * HeroBanner block component
 */
export function HeroBannerBlock(props: HeroReference): JSX.Element {
	const sections = [
		{
			condition: props.party_and_consultant_connected,
			data: {
				description:
					props.party_and_consultant_connected
						?.party_and_consultant_connected_description,
				cta: props.party_and_consultant_connected?.cta
			}
		},
		{
			condition: props.consultant_connected_section,
			data: {
				description:
					props.consultant_connected_section?.consultant_description,
				cta: props.consultant_connected_section?.consultant_cta
			}
		},
		{
			condition: props.find_a_consultant_section,
			data: {
				image: props.find_a_consultant_section?.find_a_consultant_image
					?.image,
				imageAltText:
					props.find_a_consultant_section?.find_a_consultant_image
						?.image_alt_text,
				description:
					props.find_a_consultant_section
						?.find_a_consultant_description,
				cta: props.find_a_consultant_section?.find_a_consultant_cta
			}
		}
	];

	const primaryCTA = props.call_to_action?.[0];
	const secondaryCTA = props.call_to_action?.[1];
	const consultantInfo: ConsultantInfo | undefined = sections
		.filter((section) => section.condition)
		.map((section) => section.data)[0];

	return (
		<HeroBanner<NextLinkProps, NextImageProps>
			headline={props.headline?.headline || ""}
			coverImage={{
				component: NextImage,
				...resolveImageProps("16:9", props.image)
			}}
			description={props.description || ""}
			consultant={{
				description: consultantInfo?.description,
				avatarImage: {
					component: NextImage,
					src: consultantInfo?.image || "",
					alt: consultantInfo?.imageAltText || ""
				},
				defaultImage: {
					component: NextImage,
					src: consultantInfo?.image || "",
					alt: consultantInfo?.imageAltText || ""
				}
			}}
			primaryCta={{
				component: NextLink,
				children: primaryCTA?.link?.title,
				href: primaryCTA?.link?.href,
				target: primaryCTA?.open_new_tab ? "_blank" : "_self"
			}}
			secondaryCta={{
				component: NextLink,
				children: secondaryCTA?.link?.title,
				href: secondaryCTA?.link?.href,
				target: secondaryCTA?.open_new_tab ? "_blank" : "_self"
			}}
			consultantCta={{
				component: NextLink,
				children: consultantInfo?.cta?.link?.title,
				href: consultantInfo?.cta?.link?.href,
				target: consultantInfo?.cta?.open_new_tab ? "_blank" : "_self"
			}}
			theme={props.theme}
		/>
	);
}
